import * as PIXI from 'pixi.js';
import { TextDataModel } from 'flux-diagram-composer';
import { Logger } from 'flux-core';
import { ModifierUtils, StateService } from 'flux-core';
import { IRectangle, IText } from 'flux-definition';
import { Rectangle } from 'flux-core';
import html2canvas from 'html2canvas';
import { Observable } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { TiptapDocumentsManagerShapeText } from '../../base/ui/text-editor/tiptap-documents-manager-shape-text.cmp';
import { InteractionSubContext } from '../../base/interaction/cursor-drawable.i';
import { ICursor } from '../../base/interaction/cursor-drawable.i';
import { get } from 'lodash';

// Import font URLs map from EaselJS version
import { fontsUrlsMap } from '../easeljs/tiptap-text-view';
import { PixiHelpers } from './pixi-helpers';

/**
 * PixiTiptapTextView is to render tiptap content on the canvas using PIXI
 * by means of the html2canvas module
 */
export class PixiTiptapTextView extends PIXI.Sprite {

    public static state: StateService<any, any>;

    /**
     * The width of the text to start text wrapping
     */
    public lineWidth: number;

    public _html: string;

    protected __bounds;
    protected transform;
    protected change;

    protected diagramId;

    protected renderingScale = 1;

    public __width: number;
    public __height: number;

    protected canvas;
    protected ctx: CanvasRenderingContext2D;

    protected quality =  2;
    // public name;    

    /**
     * Number of pixels to add as padding when caching the text view.
     */
    protected static CACHE_PADDING = 10;

     /**
      * The pixel ratio of the cached image.
      */
    protected static CACHE_DENSITY = 2;

    /**
     * Added to check if this object is PixiTiptapTextView
     */
    public isTiptapTextView: boolean = true;

    protected _sprite: PIXI.Sprite;

    constructor(protected model: TextDataModel, protected shapeId) {
        super();
        this.eventMode = 'static';
        this.html = model.html;
        this.name = model.id;
        this.diagramId = PixiTiptapTextView.state?.get('CurrentDiagram');
        this.canvas = document.createElement("canvas");
        this.canvas.width = 0;
        this.canvas.height = 0;
        // document.body.appendChild(this.canvas);
        // // this.canvas.width = 500;
        // // this.canvas.height = 500;
        // this.canvas.style.position = 'absolute';
        // this.canvas.style.top = '0px';
        // this.canvas.style.left = '0px';
        // this.canvas.style.zIndex = '1000';
        
        this.ctx = this.canvas.getContext("2d"); // Get 2D context
        // this.texture = PIXI.Texture.from(this.canvas);
    }

    public set html(value: string) {
        this._html = value;
    }

    public set width(value: number) {
        this.__width = value;
    }

    /**
     * Render the text using html2canvas
     */
    /**
     * Overrding the easeljs DisplayObject's draw function
     */
    public draw( ctx = this.ctx ) {
        TiptapDocumentsManagerShapeText.initialized.pipe(
            filter( dId => this.diagramId === dId ),
            take( 1 ),
            switchMap(() =>
                TiptapDocumentsManagerShapeText.getHtmlElement( this.diagramId, this.shapeId, this.model.id )),
            tap(( domNode: HTMLElement ) =>  {
                if ( !( domNode )) {
                    return;
                }
                const textDomVisible = TiptapDocumentsManagerShapeText
                    .isShapeTextDomViewVisible( this.diagramId, this.shapeId, this.model.id );
                if ( !textDomVisible ) {
                    this.prepareHTMLNode();
                }
                const bounds = TiptapDocumentsManagerShapeText.getTextBounds( this.diagramId, this.shapeId, this.model.id );
                const quality = PixiHelpers.getWebglSupportedQuality( this.quality, this.lineWidth, bounds.height  );
                this.canvas.width = this.lineWidth * quality;
                this.canvas.height = bounds.height * quality;
                this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
                this.ctx.imageSmoothingEnabled = true;
                this.ctx.imageSmoothingQuality = 'high';
                this.scale.set( 1 / quality );


                // Added to fix text position issue
                // https://github.com/niklasvh/html2canvas/issues/2775
                const s = document.createElement( 'style' );
                document.head.appendChild( s );
                s.sheet?.insertRule( 'body > div:last-child img { display: inline-block; }' );
                html2canvas( domNode , {
                    // width: this.lineWidth * this.quality,
                    // height: 2000,
                    useCORS: true,
                    logging: false,
                    allowTaint : true,
                    canvas: this.canvas,
                    // x: -PixiTiptapTextView.CACHE_PADDING,
                    // y: -PixiTiptapTextView.CACHE_PADDING,
                    x: 0,
                    y: 0,
                    ignoreElements: el => {
                        const tagName = el.nodeName.toLowerCase();
                        if ( tagName === 'head' ) {
                            return false;
                        }
                        if ( el?.parentElement.nodeName === 'HEAD' ) {
                            if ( tagName === 'base' ) {
                                return false;
                            }
                            if ( tagName === 'link' ) {
                                return false;
                            }
                            if ( tagName === 'style' ) {
                                return !el.textContent.includes( '@font-face' );
                            }
                            return true;
                        }

                        if ( el.querySelector( 'shape-text-editor' )) {
                            return false;
                        } else if ( tagName === 'shape-text-editor' ) {
                            return false;
                        } else {
                            const shapeTextEditorEl = document.querySelector( 'shape-text-editor' );
                            if ( el.classList.contains( 'tiptap-table-toolbar' )) {
                                return true;
                            }
                            if ( tagName === 'tiptap-hyperlink-editor' ) {
                                return true;
                            }
                            if ( el.classList.contains( 'tiptap-child-editor' ) &&
                                el.getAttribute( 'data-editor-id' ) !== `${this.shapeId}-${this.name}` ) {
                                return true;
                            }
                            return !shapeTextEditorEl.contains( el );
                        }
                    },
                    onclone: async c => {
                        const style = document.createElement( 'style' );
                        style.innerHTML = `
                            .tiptap-child-editor-content { transform: scale(1) !important; }
                            .tiptap-child-editor { display: block !important; visibility: visible !important; content-visibility: visible !important; transform: scale(1) !important; }
                            .collaboration-cursor__caret, p > .ProseMirror-separator, p > .ProseMirror-trailingBreak { display: none !important; }
                            .ProseMirror-yjs-selection { background-color: transparent !important; }`;
                        c.body.appendChild( style );
                        if ( !textDomVisible ) {
                            this.restoreHTMLNode();
                        }

                        // NOTE: Store fonts in the local storage and apply to the cloned document
                        // to fix text rendering issues occur when the font is not fetchalbe from the server.
                        // https://github.com/niklasvh/html2canvas/issues/1940
                        const fontsToLoad = new Set<string>();
                        Array.from(
                            c.querySelectorAll( '.tiptap-child-editor-content *' ),
                        ).forEach(( e: any ) => {
                            fontsToLoad.add( e.style.fontFamily );
                        });
                        const fontsArray = Array.from( fontsToLoad );
                        await this.useFontsFromLocalStorage( c, fontsArray ).catch( e => {
                            Logger.error( 'Tiptap text rendering font cache error:', e );
                        });
                    },
                    backgroundColor: null,
                    scale: quality, // This needs to be fixed
                }).then( canvas => {
                    const { open, shapeId, textId } = PixiTiptapTextView.state.get( 'EditingText' );
                    if ( !( open && shapeId === this.shapeId && textId === this.model.id )) {
                        TiptapDocumentsManagerShapeText.textRenderingState.next({
                            shapeId: this.shapeId,
                            state: 'completed',
                            textId: this.model.id,
                        });
                    }
                    // TiptapTextView.state.set( 'UpdateDiagramCanvas', true );

                    
                    this.texture.destroy();
                    this.texture = PIXI.Texture.from(canvas);
                    this.texture.update();
                    s.remove();
                    this.updatePos();
                }).catch( e => {
                    s.remove();
                    TiptapDocumentsManagerShapeText.textRenderingState.next({
                        shapeId: this.shapeId,
                        state: 'failed',
                        textId: this.model.id,

                    });
                    if ( !textDomVisible ) {
                        this.restoreHTMLNode();
                    }
                });
            }),
        ).subscribe();
        return true;
    }


    /**
     * If the html text is hidden ( If the shape text editor is closed )
     * we have to make it available for rendering. By changing the zIndex, the
     * shape text element won't be visible to the user while rendering is in progress
     */
    protected prepareHTMLNode() {
        TiptapDocumentsManagerShapeText.makeShapeTextNodeRenderable(
            this.diagramId,
            this.shapeId,
            this.model.id,
        );
        TiptapDocumentsManagerShapeText.setShapeTextNodeZoomlevel(
            this.diagramId,
            this.shapeId,
            this.model.id,
            'reset',
        );
    }

    /**
     * Reset the values after rendering is done
     */
    protected restoreHTMLNode() {
        TiptapDocumentsManagerShapeText.setShapeTextNodeZoomlevel(
            this.diagramId,
            this.shapeId,
            this.model.id,
            'restore',
        );
        TiptapDocumentsManagerShapeText.setShapeTextNodeVisibility(
            this.diagramId,
            this.shapeId,
            this.model.id,
            'hidden',
        );
    }

    public destroy() {
        if ( !this.parent ) {
            Logger.error( 'TiptapTextView.destroy called but this.parent was null.' );
            return;
        }
        this.parent.removeChild( this );
    }

    /**
     * Returns the cursor and tail defined for the given context.
     * The return value may be null if no cursor is availbale for the given context
     */
     public getCursor( contexts: InteractionSubContext[]): ICursor {
        if (
            contexts.includes( 'editor' ) &&
            contexts.includes( 'normal' ) &&
            contexts.includes( 'selected' )
        ) {
            return { cursor: 'text', tail: null, type: 'css' };
        }
        return { cursor: 'default', tail: null, type: 'css', viewingArea: 'app' };
    }

    public getShapeTextBounds(): Observable<Rectangle> {
        return TiptapDocumentsManagerShapeText.getHtmlElement( this.diagramId, this.shapeId, this.model.id ).pipe(
            map(( domNode: HTMLElement ) =>
                TiptapDocumentsManagerShapeText.getTextBounds( this.diagramId, this.shapeId, this.model.id ) as any,
            ),
        );
    }

    /**
     * The width and height of the cached bitmap image of the shape.
     */
    protected cachedBounds: { width: number, height: number } = null;

    public updateViewCache( model: IText, change?: any ) {

        let shouldUpdate = false;
        if ( this.cachedBounds ) {
            if ( ModifierUtils.hasChanges( change, `texts.${model.id}.html` )) {
                const newHtml = get( change, `$set.texts.${model.id}.html` )
                    || change.$set[ `texts.${model.id}.html` ];
                if ( newHtml !== this.model.html ) {
                    // this.cacheAsTexture( false );
                    shouldUpdate = true;
                    this.cachedBounds = null;
                }
            } else if ( ModifierUtils.hasChanges( change, `texts.${model.id}.wrapWidth` )) {
                const newWrapWidth = get( change, `$set.texts.${model.id}.wrapWidth` )
                    || change.$set[ `texts.${model.id}.wrapWidth` ];
                if ( newWrapWidth !== ( this.model as any ).wrapWidth ) {
                    // this.cacheAsTexture( false );
                    shouldUpdate = true;
                    this.cachedBounds = null;
                }
            }
        }
        if ( !this.cachedBounds ) {
            if ( !( model as any ).html ) {
                return;
            }
            shouldUpdate = true;
            this.getShapeTextBounds().subscribe( bounds => {
                // this.cache(
                //     -PixiTiptapTextView.CACHE_PADDING,
                //     -PixiTiptapTextView.CACHE_PADDING,
                //     bounds.width + 2 * PixiTiptapTextView.CACHE_PADDING,
                //     bounds.height + 2 * PixiTiptapTextView.CACHE_PADDING,
                //     PixiTiptapTextView.CACHE_DENSITY,
                // );
                // this.cacheAsTexture( true );
                this.cachedBounds = { width: bounds.width, height: bounds.height };
            });
        }
        return shouldUpdate;
    }

    public resetCache() {
        // this.uncache();
        this.cachedBounds = null;
    }

    public update( model: TextDataModel, _change?: any ) {
    }

    public updateHitArea( model: IText, rect: IRectangle ) {
        // const bounds = Rectangle.from( rect );
        // const hitArea = this.hitArea as Shape;
        // hitArea.graphics.clear();
        // hitArea.graphics.beginFill( '#000' );
        // if ( model.hitArea ) {
        //     hitArea.graphics.drawRect(
        //         // Note: After enabling partial text alignments, the width of the text view
        //         // is equal to hit area width so x, y can be 0 always.
        //         0,
        //         0,
        //         model.hitArea.width,
        //         model.hitArea.height,
        //     );
        // } else if ( model.editable ) {
        //     hitArea.graphics.drawRect( bounds.x, bounds.y, this.lineWidth, bounds.height );
        // }
        // hitArea.graphics.endFill();
    }

    protected updatePos() {
        // Tobe overridden
    }

    /**
     * This method stores the fonts ( if not stored already ) in the local storage and
     * apply to the cloned document.
     * @param c ClonedDocument
     * @param fontNames Font names to be fetched and stored
     */
    protected async useFontsFromLocalStorage( c: Document, fontNames: string []) {
        for ( let index = 0; index < fontNames.length; index++ ) {
            const name = fontNames[index];
            const _urls = fontsUrlsMap[ name ] || [];
            for ( let i = 0; i < _urls.length; i++ )  {
                const url = _urls[i];
                let fontData = localStorage.getItem( url );

                // Fetch and store if not available in the local storage
                if ( !fontData ) {
                    const response = await fetch( url );
                    const blob = await response.blob();
                    const reader = new FileReader();
                    reader.readAsDataURL( blob );
                    await new Promise( resolve => {
                        reader.onload = ( event: any ) => {
                            localStorage.setItem( url, event.target.result );
                            resolve( true );
                        };
                    });
                    fontData = localStorage.getItem( url );
                }

                // Apply the font to the document
                if ( fontData ) {
                    const format = url.split( '.' )[2];
                    // @ts-ignore
                    const fontFace = new FontFace( name, `url('${fontData}') format('${format}')` );
                    const loadedFace = await fontFace.load();
                    ( c as any ).fonts.add( loadedFace );
                }
            }

            // Add style tag to header
            const srcUrl = _urls
                .map( url => `url('${localStorage.getItem( url )}') format('${url.split( '.' )[2]}')` ).join( ', ' );
            if ( srcUrl ) {
                const st = c.createElement( 'style' );
                st.innerHTML = `@font-face {
                        font-family: '${name}';
                        src: ${srcUrl};
                        font-weight: normal;
                        font-style: normal;
                        font-display: swap;
                    }`;
                c.head.appendChild( st );
            }
        }
    }
}